import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

// Age gate action types
const AGE_GATE_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_VERIFIED: 'SET_VERIFIED',
  SET_DENIED: 'SET_DENIED',
  RESET: 'RESET'
};

// Initial age gate state
const initialState = {
  isVerified: false,
  isLoading: true,
  isDenied: false
};

// Age gate reducer
const ageGateReducer = (state, action) => {
  switch (action.type) {
    case AGE_GATE_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case AGE_GATE_ACTIONS.SET_VERIFIED:
      return {
        ...state,
        isVerified: true,
        isLoading: false,
        isDenied: false
      };

    case AGE_GATE_ACTIONS.SET_DENIED:
      return {
        ...state,
        isVerified: false,
        isLoading: false,
        isDenied: true
      };

    case AGE_GATE_ACTIONS.RESET:
      return {
        ...initialState,
        isLoading: false
      };

    default:
      return state;
  }
};

// Create age gate context
const AgeGateContext = createContext(undefined);

// Age gate provider component
export const AgeGateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(ageGateReducer, initialState);

  // Helper function to check if age verification is valid
  const isAgeVerificationValid = useCallback(() => {
    try {
      const verified = localStorage.getItem('age_verified');
      const verifiedDate = localStorage.getItem('age_verified_date');
      
      if (!verified || verified !== 'true' || !verifiedDate) {
        return false;
      }

      // Check if verification is older than 30 days (optional security measure)
      const verificationDate = new Date(verifiedDate);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      return verificationDate > thirtyDaysAgo;
    } catch (error) {
      console.error('Error checking age verification:', error);
      return false;
    }
  }, []);

  // Helper function to clear age verification
  const clearAgeVerification = useCallback(() => {
    try {
      localStorage.removeItem('age_verified');
      localStorage.removeItem('age_verified_date');
    } catch (error) {
      console.error('Error clearing age verification:', error);
    }
  }, []);

  // Check age verification status on app load
  useEffect(() => {
    const checkAgeVerification = () => {
      if (isAgeVerificationValid()) {
        dispatch({ type: AGE_GATE_ACTIONS.SET_VERIFIED });
      } else {
        dispatch({ type: AGE_GATE_ACTIONS.SET_LOADING, payload: false });
      }
    };

    // Use setTimeout to ensure the effect runs after initial render
    const timeoutId = setTimeout(checkAgeVerification, 0);
    return () => clearTimeout(timeoutId);
  }, [isAgeVerificationValid]);

  // Confirm age verification
  const confirmAge = useCallback(() => {
    try {
      localStorage.setItem('age_verified', 'true');
      localStorage.setItem('age_verified_date', new Date().toISOString());
      dispatch({ type: AGE_GATE_ACTIONS.SET_VERIFIED });
    } catch (error) {
      console.error('Error storing age verification:', error);
      // Still dispatch verified even if localStorage fails
      dispatch({ type: AGE_GATE_ACTIONS.SET_VERIFIED });
    }
  }, []);

  // Deny age verification
  const denyAge = useCallback(() => {
    dispatch({ type: AGE_GATE_ACTIONS.SET_DENIED });
  }, []);

  // Reset age gate (for testing or logout scenarios)
  const resetAgeGate = useCallback(() => {
    clearAgeVerification();
    dispatch({ type: AGE_GATE_ACTIONS.RESET });
  }, [clearAgeVerification]);

  // Context value
  const value = {
    // State
    isVerified: state.isVerified,
    isLoading: state.isLoading,
    isDenied: state.isDenied,
    
    // Actions
    confirmAge,
    denyAge,
    resetAgeGate,
    
    // Utilities
    isAgeVerificationValid
  };

  return (
    <AgeGateContext.Provider value={value}>
      {children}
    </AgeGateContext.Provider>
  );
};

// Custom hook to use age gate context
export const useAgeGate = () => {
  const context = useContext(AgeGateContext);
  
  if (context === undefined) {
    throw new Error('useAgeGate must be used within an AgeGateProvider');
  }
  
  return context;
};

export default AgeGateContext;
